import React, { useState, useEffect, useCallback } from 'react';
import { 
  ArrowLeft,
  User,
  Mail,
  Calendar,
  MessageSquare,
  Key,
  Edit,
  Trash2,
  Activity,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { adminAPI } from '../services/api';
import { User as UserType, UserDetails, UserDetailsResponse, Conversation } from '../types';
import EditUserModal from '../components/EditUserModal';
import ResetPasswordModal from '../components/ResetPasswordModal';

interface UserProfileProps {
  userId: number;
  onBack: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ userId, onBack }) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [expandedConversations, setExpandedConversations] = useState<Set<number>>(new Set());
  const [conversationsPage, setConversationsPage] = useState(1);
  const [totalConversations, setTotalConversations] = useState(0);

  const loadUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      const [userData, detailsData] = await Promise.all([
        adminAPI.getUserDetails(userId),
        adminAPI.getUserConversations(userId, 1, 5) // Charger les 5 premières conversations
      ]);
      
      // L'API retourne { user: {...}, conversations: ..., messageCount: ..., activityStats: ... }
      // On doit reconstruire l'objet UserDetails
      const userDetailsData: UserDetails = {
        ...userData.user,
        conversations: userData.conversations,
        messageCount: userData.messageCount,
        activityStats: userData.activityStats
      };
      
      setUserDetails(userDetailsData);
      setConversations(detailsData.conversations);
      setTotalConversations(detailsData.pagination.totalConversations);
    } catch (error) {
      console.error('Error loading user profile:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadMoreConversations = useCallback(async () => {
    try {
      setConversationsLoading(true);
      const data = await adminAPI.getUserConversations(userId, conversationsPage + 1, 10);
      setConversations(prev => [...prev, ...data.conversations]);
      setConversationsPage(prev => prev + 1);
    } catch (error) {
      console.error('Error loading more conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  }, [userId, conversationsPage]);

  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  const toggleConversation = (conversationId: number) => {
    const newExpanded = new Set(expandedConversations);
    if (newExpanded.has(conversationId)) {
      newExpanded.delete(conversationId);
    } else {
      newExpanded.add(conversationId);
    }
    setExpandedConversations(newExpanded);
  };

  const handleEditUser = async (userId: number, userData: any) => {
    try {
      await adminAPI.updateUser(userId, userData);
      setShowEditModal(false);
      loadUserProfile(); // Recharger les données
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleResetPassword = async (userId: number, newPassword: string) => {
    try {
      await adminAPI.resetUserPassword(userId, { newPassword });
      setShowResetModal(false);
    } catch (error) {
      console.error('Error resetting password:', error);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.')) {
      try {
        await adminAPI.deleteUser(userId);
        onBack(); // Retourner à la liste
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMessageDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-FR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!userDetails) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Utilisateur non trouvé</h3>
        <p className="mt-1 text-sm text-gray-500">
          L'utilisateur demandé n'existe pas ou a été supprimé.
        </p>
        <button
          onClick={onBack}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </button>
            <div>
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Profil Utilisateur
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Gestion complète du profil et des données utilisateur
              </p>
            </div>
          </div>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            onClick={() => loadUserProfile()}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations utilisateur */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Informations</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Avatar et nom */}
              <div className="flex items-center space-x-4">
                <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-xl font-medium text-primary-600">
                    {userDetails.firstName?.charAt(0) || 'U'}{userDetails.lastName?.charAt(0) || 'U'}
                  </span>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-900">
                    {userDetails.firstName || 'Prénom'} {userDetails.lastName || 'Nom'}
                  </h4>
                  {userDetails.isAdmin && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Administrateur
                    </span>
                  )}
                </div>
              </div>

              {/* Détails */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-900">{userDetails.email}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-500">
                    Créé le {formatDate(userDetails.createdAt)}
                  </span>
                </div>
                {userDetails.lastLoginAt && (
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      Dernière connexion: {formatDate(userDetails.lastLoginAt)}
                    </span>
                  </div>
                )}
              </div>

              {/* Statistiques */}
              <div className="border-t border-gray-200 pt-4">
                <h5 className="text-sm font-medium text-gray-900 mb-3">Statistiques</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary-600">
                      {userDetails.activityStats.totalConversations}
                    </div>
                    <div className="text-xs text-gray-500">Conversations</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {userDetails.activityStats.totalMessages}
                    </div>
                    <div className="text-xs text-gray-500">Messages</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Actions</h3>
            </div>
            <div className="px-6 py-4 space-y-3">
              <button
                onClick={() => setShowEditModal(true)}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Edit className="h-4 w-4 mr-2" />
                Modifier le profil
              </button>

              <button
                onClick={() => setShowResetModal(true)}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Key className="h-4 w-4 mr-2" />
                Réinitialiser mot de passe
              </button>

              <button
                onClick={() => handleDeleteUser(userDetails.id)}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer l'utilisateur
              </button>
            </div>
          </div>
        </div>

        {/* Conversations */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Conversations ({totalConversations})
                </h3>
                {conversations.length < totalConversations && (
                  <button
                    onClick={loadMoreConversations}
                    disabled={conversationsLoading}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    {conversationsLoading ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <MessageSquare className="h-4 w-4 mr-2" />
                    )}
                    Charger plus
                  </button>
                )}
              </div>
            </div>

            {conversations.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune conversation</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Cet utilisateur n'a pas encore de conversations.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {conversations.map((conversation) => {
                  const isExpanded = expandedConversations.has(conversation.id);
                  const userMessages = conversation.messages.filter(msg => msg.isUserMessage);
                  const assistantMessages = conversation.messages.filter(msg => !msg.isUserMessage);
                  
                  return (
                    <div key={conversation.id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => toggleConversation(conversation.id)}
                              className="flex items-center space-x-2 text-left hover:text-primary-600"
                            >
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                              <h4 className="text-lg font-medium text-gray-900 truncate">
                                {conversation.title}
                              </h4>
                            </button>
                          </div>
                          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {formatDate(conversation.created_at)}
                            </span>
                            <span className="flex items-center">
                              <MessageSquare className="h-4 w-4 mr-1" />
                              {conversation.messages.length} message(s)
                            </span>
                            <span className="text-green-600">
                              {userMessages.length} question(s)
                            </span>
                            <span className="text-blue-600">
                              {assistantMessages.length} réponse(s)
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Messages détaillés */}
                      {isExpanded && (
                        <div className="mt-4 border-t border-gray-200 pt-4">
                          <div className="space-y-4">
                            {conversation.messages.map((message, index) => (
                              <div
                                key={message.id}
                                className={`flex ${message.isUserMessage ? 'justify-end' : 'justify-start'}`}
                              >
                                <div
                                  className={`max-w-3xl px-4 py-2 rounded-lg ${
                                    message.isUserMessage
                                      ? 'bg-primary-600 text-white'
                                      : 'bg-gray-100 text-gray-900'
                                  }`}
                                >
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="text-xs font-medium opacity-75">
                                      {message.isUserMessage ? 'Utilisateur' : 'Assistant'}
                                    </span>
                                    <span className="text-xs opacity-75">
                                      {formatMessageDate(message.createdAt)}
                                    </span>
                                  </div>
                                  <div className="whitespace-pre-wrap text-sm">
                                    {message.content}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && userDetails && (
        <EditUserModal
          user={userDetails}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleEditUser}
        />
      )}

      {showResetModal && userDetails && (
        <ResetPasswordModal
          user={userDetails}
          onClose={() => setShowResetModal(false)}
          onSubmit={handleResetPassword}
        />
      )}
    </div>
  );
};

export default UserProfile;
